import {
    getDailyFileUploads,
} from '@/api/systemAnalytics.js';
import constants from '@/constants';

export default {
    state: {
        dailyUploads: [],
        isLoading: false,

        // Date range filters
        dateRange: {
            startDate: null,
            endDate: null,
        },

        // Pagination (page-based)
        pagination: {
            total: 0,
            page: 1,
            perPage: 50,
            totalPages: 0,
        },

        // Sorting
        sortBy: 'upload_date',
        sortOrder: 'desc',
    },

    getters: {
        SYSTEM_ANALYTICS_DAILY_UPLOADS: (state) => state.dailyUploads,
        SYSTEM_ANALYTICS_LOADING: (state) => state.isLoading,
        SYSTEM_ANALYTICS_DATE_RANGE: (state) => state.dateRange,
        SYSTEM_ANALYTICS_PAGINATION: (state) => state.pagination,
        SYSTEM_ANALYTICS_SORT_BY: (state) => state.sortBy,
        SYSTEM_ANALYTICS_SORT_ORDER: (state) => state.sortOrder,
        SYSTEM_ANALYTICS_CURRENT_PAGE: (state) => state.pagination.page,
        SYSTEM_ANALYTICS_TOTAL_PAGES: (state) => state.pagination.totalPages,
    },

    mutations: {
        SET_SYSTEM_ANALYTICS_DAILY_UPLOADS(state, data) {
            state.dailyUploads = data;
        },
        SET_SYSTEM_ANALYTICS_LOADING(state, value) {
            state.isLoading = value;
        },
        SET_SYSTEM_ANALYTICS_DATE_RANGE(state, { startDate, endDate }) {
            state.dateRange = { startDate, endDate };
        },
        SET_SYSTEM_ANALYTICS_PAGINATION(state, pagination) {
            state.pagination = {
                total: pagination.total || 0,
                page: pagination.page || 1,
                perPage: pagination.per_page || 50,
                totalPages: pagination.total_pages || 0,
            };
        },
        SET_SYSTEM_ANALYTICS_PAGE(state, page) {
            state.pagination.page = page;
        },
        SET_SYSTEM_ANALYTICS_SORT(state, { sortBy, sortOrder }) {
            state.sortBy = sortBy;
            state.sortOrder = sortOrder;
        },
        RESET_SYSTEM_ANALYTICS(state) {
            state.dailyUploads = [];
            state.pagination = {
                total: 0,
                page: 1,
                perPage: 50,
                totalPages: 0,
            };
            state.sortBy = 'upload_date';
            state.sortOrder = 'desc';
        },
    },

    actions: {
        async loadSystemAnalyticsDailyUploads({ commit, state }) {
            commit('SET_SYSTEM_ANALYTICS_LOADING', true);

            try {
                const params = {
                    page: state.pagination.page,
                    per_page: state.pagination.perPage,
                    sort_by: state.sortBy,
                    sort_order: state.sortOrder,
                };

                // Add date range if provided
                if (state.dateRange.startDate) {
                    params.start_date = state.dateRange.startDate;
                }
                if (state.dateRange.endDate) {
                    params.end_date = state.dateRange.endDate;
                }

                const response = await getDailyFileUploads(params);

                if (response) {
                    // Handle response structure: could be { data: [...] } or direct array
                    let uploadData = response.data || response;

                    // Ensure uploadData is an array
                    if (!Array.isArray(uploadData)) {
                        uploadData = [];
                    }

                    commit('SET_SYSTEM_ANALYTICS_DAILY_UPLOADS', uploadData);

                    // Calculate pagination based on actual data length
                    const total = uploadData.length;
                    const perPage = state.pagination.perPage;
                    const totalPages = Math.ceil(total / perPage) || 1;

                    commit('SET_SYSTEM_ANALYTICS_PAGINATION', {
                        total,
                        page: state.pagination.page,
                        per_page: perPage,
                        total_pages: totalPages,
                    });
                }
            } catch (error) {
                console.error('Error loading system analytics daily uploads:', error);
                commit('SET_SYSTEM_ANALYTICS_DAILY_UPLOADS', []);
            } finally {
                commit('SET_SYSTEM_ANALYTICS_LOADING', false);
            }
        },

        async changeSystemAnalyticsPage({ commit, dispatch }, page) {
            commit('SET_SYSTEM_ANALYTICS_PAGE', page);
            await dispatch('loadSystemAnalyticsDailyUploads');
        },

        async setSystemAnalyticsDateRange({ commit, dispatch }, { startDate, endDate }) {
            commit('SET_SYSTEM_ANALYTICS_DATE_RANGE', { startDate, endDate });
            commit('SET_SYSTEM_ANALYTICS_PAGE', 1);
            await dispatch('loadSystemAnalyticsDailyUploads');
        },

        setSystemAnalyticsSort({ commit, dispatch }, { sortBy, sortOrder }) {
            commit('SET_SYSTEM_ANALYTICS_SORT', { sortBy, sortOrder });
            commit('SET_SYSTEM_ANALYTICS_PAGE', 1);
            dispatch('loadSystemAnalyticsDailyUploads');
        },
    },
};
